<template>
    <div class="flex flex-col gap-4" data-testid="home-mywork-trends-detail">
        <div class="flex flex-col gap-1">
            <h2 class="title-section text-valence-grey-900">Your {{ progress.weeks }}-week trends</h2>
            <p class="body-small text-valence-grey-600">{{ progress.period_range_label }}</p>
            <p class="body-small text-valence-grey-600">See how your working patterns have changed week by week.</p>
        </div>

        <div class="flex flex-col gap-1">
            <p class="body-semibold text-valence-grey-900">{{ headline.phrase || `${headline.label} is shifting` }}</p>
            <p v-if="headline.context" class="body-small text-valence-grey-600">{{ headline.context }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-for="metric in progress.metrics" :key="metric.metric" class="rounded-lgr border border-valence-grey-100 p-3 flex flex-col gap-2">
                <div class="flex items-center justify-between gap-2">
                    <span class="body-small-semibold text-valence-grey-900">{{ metric.label }}</span>
                    <span class="body-small-semibold" :class="trendClass(metric.trend)">
                        {{ formatValue(metric.recent) }}
                        <span v-if="metric.pct_change !== null">({{ formatPct(metric.pct_change) }})</span>
                    </span>
                </div>
                <div class="relative h-20">
                    <canvas :ref="(el) => setCanvasRef(metric.metric, el)"></canvas>
                </div>
                <p class="body-small text-valence-grey-600">{{ metric.observation }}</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { MyWorkProgressData, MyWorkProgressMetric } from "~vue/types/chat_home";
import { CategoryScale, Chart, LinearScale, LineController, LineElement, PointElement } from "chart.js";
import { onMounted, onUnmounted, watch } from "vue";

import { cssColor } from "./workiqTrendColors";

const props = defineProps<{
    progress: MyWorkProgressData;
    headline: MyWorkProgressData["headline"];
}>();

const canvases = new Map<string, HTMLCanvasElement>();
const charts = new Map<string, Chart>();

function setCanvasRef(metricKey: string, el: unknown) {
    if (el instanceof HTMLCanvasElement) {
        canvases.set(metricKey, el);
    }
}

function formatValue(value: number | null): string {
    if (value === null) {
        return "—";
    }
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatPct(pct: number): string {
    const rounded = Math.round(pct * 100);
    return `${rounded > 0 ? "+" : ""}${rounded}%`;
}

// Kept neutral in the chart line itself; see HomeMyWorkProgressCard for why observed shape and
// coaching verdict stay visually separate. The value text is the one place a verdict is shown.
function trendClass(trend: string): string {
    if (trend === "improving") {
        return "text-valence-green-800";
    }
    if (trend === "declining") {
        return "text-valence-coral-800";
    }
    return "text-valence-grey-600";
}

function renderChart(metric: MyWorkProgressMetric) {
    const canvas = canvases.get(metric.metric);
    if (!canvas) {
        return;
    }
    charts.get(metric.metric)?.destroy();
    if (!metric.series.length) {
        return;
    }

    const color = cssColor("--color-valence-blue", "#003fdc");
    const chart = new Chart(canvas, {
        type: "line",
        data: {
            labels: metric.series.map((point) => point.period),
            datasets: [
                {
                    data: metric.series.map((point) => point.value),
                    borderColor: color,
                    backgroundColor: `${color}1a`,
                    fill: true,
                    tension: 0.3,
                    pointRadius: 2,
                    borderWidth: 2,
                },
            ],
        },
        options: {
            animation: false,
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: { ticks: { display: false }, grid: { display: false } },
                y: { ticks: { color: "#616161", font: { family: "Inter, sans-serif", size: 10 }, maxTicksLimit: 3 }, grid: { display: false } },
            },
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
            },
        },
    });
    charts.set(metric.metric, chart);
}

function renderAll() {
    for (const metric of props.progress.metrics) {
        renderChart(metric);
    }
}

watch(() => props.progress, renderAll, { deep: true });

onMounted(() => {
    Chart.register(LineController, LinearScale, CategoryScale, PointElement, LineElement);
    renderAll();
});

onUnmounted(() => {
    for (const chart of charts.values()) {
        chart.destroy();
    }
    charts.clear();
});
</script>
